/**
 * Rezeptbilder offline — einmal gesehen, auch ohne Netz da (Küche, Supermarkt).
 *
 * Die Adresse trägt `?v=<imageAt>`: Ein neues Bild hat eine neue Adresse, ein
 * gespeichertes ist deshalb nie veraltet — zuerst aus dem Speicher, sonst aus
 * dem Netz und dann gemerkt. Ältere Fassungen desselben Gerichts gehen, sobald
 * eine neue gespeichert wird; insgesamt höchstens `MAX_ENTRIES`.
 *
 * Läuft im Service Worker; als eigenes Modul, damit es sich ohne ihn testen lässt.
 */

export const RECIPE_IMAGE_CACHE = 'kraftwerk-recipe-images';
export const MAX_ENTRIES = 300;

const PATH = /\/recipes\/([0-9a-f-]{36})\/image$/i;

/** Rezept-Kennung, wenn es ein Bildabruf mit Versionsmarke ist — sonst `null`. */
export function recipeImageId(url: URL, method: string): string | null {
  if (method !== 'GET' || !url.searchParams.has('v')) return null;
  return PATH.exec(url.pathname)?.[1] ?? null;
}

export interface CacheLike {
  match(request: Request): Promise<Response | undefined>;
  put(request: Request, response: Response): Promise<void>;
  keys(): Promise<readonly Request[]>;
  delete(request: Request): Promise<boolean>;
}

/** Zuerst gespeichert, sonst aus dem Netz — und dann merken. Netzfehler gehen durch. */
export async function serveRecipeImage(
  request: Request,
  cache: CacheLike,
  fetchImpl: (request: Request) => Promise<Response>,
): Promise<Response> {
  const hit = await cache.match(request);
  if (hit) return hit;

  const response = await fetchImpl(request);
  // `opaque`: Bild von fremdem Ursprung ohne CORS (lokal API auf eigenem Port) —
  // nicht lesbar, aber anzeigbar und speicherbar.
  if (response.ok || response.type === 'opaque') {
    await remember(request, response.clone(), cache);
  }
  return response;
}

async function remember(request: Request, response: Response, cache: CacheLike): Promise<void> {
  const id = recipeImageId(new URL(request.url), 'GET');
  const keys = await cache.keys();
  // Ältere Fassungen desselben Gerichts: weg.
  for (const key of keys) {
    if (key.url !== request.url && recipeImageId(new URL(key.url), 'GET') === id) {
      await cache.delete(key);
    }
  }
  await cache.put(request, response);
  // Deckel: die ältesten zuerst (Cache Storage liefert in Einfügereihenfolge).
  const all = await cache.keys();
  for (const key of all.slice(0, Math.max(0, all.length - MAX_ENTRIES))) {
    await cache.delete(key);
  }
}
